import client from '@config/client'
import API_URL from '@config/services'
import { ID } from '../types'
import {
  ApiClientResponse,
  FileFolder,
  FolderPayload,
  File as FileType,
  FilePayload
} from '../types/api'

/* Folders */

/**
 * Creates a new folder
 * @param payload - Folder data
 * @returns Created folder
 */
export const createFolder = (
  payload: FolderPayload
): ApiClientResponse<FileFolder> => {
  return client
    .post(API_URL.FILES.POST_FILES_FOLDER_LIST, { ...payload })
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates an existing folder
 * @param id - Folder ID
 * @param payload - Updated folder data
 * @returns Updated folder
 */
export const updateFolder = (
  id: ID,
  payload: Partial<FolderPayload>
): ApiClientResponse<FileFolder> => {
  return client
    .patch(API_URL.FILES.PATCH_FOLDER(id), {
      ...payload,
      parent: payload?.parent
    })
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a folder
 * @param id - Folder ID to delete
 * @returns API response
 */
export const deleteFolder = (id: ID): ApiClientResponse<any> => {
  return client
    .delete(API_URL.FILES.DELETE_FOLDER(id))
    .then(response => response)
    .catch(error => error.response)
}

/* Files */

/**
 * Creates/uploads a new file
 * @param payload - File data
 * @param headers - Optional request headers
 * @returns Created file
 */
export const createFile = (
  payload: FilePayload,
  headers?: Record<string, string>
): ApiClientResponse<FileType> => {
  return client
    .post(API_URL.FILES.POST_FILES_LIST, payload, headers)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Gets a file by ID
 * @param id - File ID
 * @returns File data
 */
export const getFile = (id: ID): ApiClientResponse<FileType> => {
  return client
    .get(API_URL.FILES.GET_FILE(id))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates a file
 * @param id - File ID
 * @param payload - Updated file data
 * @param headers - Optional request headers
 * @returns Updated file
 */
export const updateFile = (
  id: ID,
  payload: Partial<FilePayload>,
  headers?: Record<string, string>
): ApiClientResponse<FileType> => {
  return client
    .patch(API_URL.FILES.PATCH_FILE(id), payload, headers)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a file
 * @param id - File ID to delete
 * @returns API response
 */
export const deleteFile = (id: ID): ApiClientResponse<any> => {
  return client
    .delete(API_URL.FILES.DELETE_FILE(id))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Downloads a file
 * @param name - Filename to save as
 * @param pk - File ID
 * @param org - Organization ID
 * @returns Promise that resolves when download completes
 */
export const downloadFile = (name: string, pk: ID, org: ID): Promise<any> => {
  return client
    .get(API_URL.FILES.DOWNLOAD_FILE + '?file_pk=' + pk + '&org=' + org, {
      responseType: 'blob'
    })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${name}.pdf`)
      document.body.appendChild(link)
      link.click()
    })
    .catch(error => error.response)
}
